// Inspired by react-hot-toast library
import * as React from "react"

import type { ToastActionElement, ToastProps } from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 5000
/** How many recent toasts the notifications list keeps (in memory, newest first). */
export const NOTIFICATION_HISTORY_LIMIT = 100

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
  type: ActionType["ADD_TOAST"]
  toast: ToasterToast
}
  | {
  type: ActionType["UPDATE_TOAST"]
  toast: Partial<ToasterToast>
}
  | {
  type: ActionType["DISMISS_TOAST"]
  toastId?: ToasterToast["id"]
}
  | {
  type: ActionType["REMOVE_TOAST"]
  toastId?: ToasterToast["id"]
}

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
              ...t,
              open: false,
            }
            : t,
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

export function closeToast(toastId: string) {
  dispatch({ type: "DISMISS_TOAST", toastId})
}

/**
 * A toast as remembered by the notifications list. Only one toast is on screen at a time
 * (`TOAST_LIMIT`), so a burst of results — a bulk CodingWiz run, a batch of status updates —
 * overwrites itself before anyone can read it; the history keeps every one of them.
 */
export interface ToastNotification {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastProps["variant"]
  created_at: Date
  read: boolean
}

/** Newest first, capped at `limit`. */
export function push_notification(
  history: ToastNotification[],
  record: ToastNotification,
  limit: number = NOTIFICATION_HISTORY_LIMIT
): ToastNotification[] {
  return [record, ...history].slice(0, limit)
}

export function mark_notifications_read(history: ToastNotification[]): ToastNotification[] {
  if (history.every(n => n.read)) {
    return history
  }
  return history.map(n => (n.read ? n : { ...n, read: true }))
}

export function unread_notification_count(history: ToastNotification[]): number {
  return history.filter(n => !n.read).length
}

let notification_history: ToastNotification[] = []
const notification_listeners: Array<(history: ToastNotification[]) => void> = []

function set_notification_history(next: ToastNotification[]) {
  notification_history = next
  notification_listeners.forEach(listener => listener(notification_history))
}

function record_notification(id: string, props: Omit<ToasterToast, "id">) {
  if (!props.title && !props.description) {
    return
  }
  set_notification_history(
    push_notification(notification_history, {
      id,
      title: props.title,
      description: props.description,
      variant: props.variant ?? "default",
      created_at: new Date(),
      read: false,
    })
  )
}

export function mark_all_notifications_read() {
  set_notification_history(mark_notifications_read(notification_history))
}

export function clear_notifications() {
  set_notification_history([])
}

/** Recent toasts, newest first, with the unread count for a badge. */
export function useToastNotifications() {
  const [history, setHistory] = React.useState<ToastNotification[]>(notification_history)

  React.useEffect(() => {
    notification_listeners.push(setHistory)
    return () => {
      const index = notification_listeners.indexOf(setHistory)
      if (index > -1) {
        notification_listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    notifications: history,
    unread_count: unread_notification_count(history),
    mark_all_read: mark_all_notifications_read,
    clear: clear_notifications,
  }
}

type Toast = Omit<ToasterToast, "id"> & {
  /** When true, the toast is not auto-dismissed; call the returned `dismiss()` when done. */
  persist?: boolean
}

function toast({ persist = false, ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  record_notification(id, props)
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      // onOpenChange: (open: any) => {
      //   if (!open) dismiss()
      // },
    },
  })
  if (!persist) {
    addToRemoveQueue(id)
  }
  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function make(id: string, read = false): ToastNotification {
    return { id, title: `t${id}`, created_at: new Date(0), read }
  }

  describe("push_notification", () => {
    it("puts the newest toast first", () => {
      const history = push_notification([make("1")], make("2"))
      expect(history.map(n => n.id)).toEqual(["2", "1"])
    })

    it("drops the oldest once the limit is reached", () => {
      let history: ToastNotification[] = []
      for (const id of ["1", "2", "3"]) {
        history = push_notification(history, make(id), 2)
      }
      expect(history.map(n => n.id)).toEqual(["3", "2"])
    })
  })

  describe("mark_notifications_read / unread_notification_count", () => {
    it("counts only unread toasts and clears them without touching read ones", () => {
      const history = [make("1"), make("2", true), make("3")]
      expect(unread_notification_count(history)).toBe(2)
      const read = mark_notifications_read(history)
      expect(unread_notification_count(read)).toBe(0)
      expect(read[1]).toBe(history[1])
    })

    it("returns the same array when nothing is unread", () => {
      const history = [make("1", true)]
      expect(mark_notifications_read(history)).toBe(history)
    })
  })
}
